import { API, API_BASE } from '../config/api';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, Send, Trash2 } from 'lucide-react';


export default function CommentSection({ itemType, itemId, isAdmin }) {
  const [comments, setComments] = useState([]);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const fetchComments = () => {
    axios.get(`${API}/comments/${itemType}/${itemId}`).then(r => setComments(r.data)).catch(() => {});
  };

  useEffect(() => { fetchComments(); }, [itemType, itemId]);

  const handlePost = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) return;
    setPosting(true);
    try {
      await axios.post(`${API}/comments`, { name: name.trim(), text: text.trim(), item_type: itemType, item_id: itemId });
      setText('');
      // Save name for next comment
      localStorage.setItem('melodrop_commenter', name.trim());
      fetchComments();
    } catch { }
    setPosting(false);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/comments/${id}`, { withCredentials: true });
      fetchComments();
    } catch { }
  };

  // Restore saved name
  useEffect(() => {
    const saved = localStorage.getItem('melodrop_commenter');
    if (saved) setName(saved);
  }, []);

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
  };

  return (
    <div className="mt-4" data-testid={`comments-${itemType}-${itemId}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-[#A3A3A3] hover:text-white transition-colors"
        data-testid={`toggle-comments-${itemId}`}
      >
        <MessageSquare className="w-4 h-4" />
        <span>{comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}</span>
      </button>

      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Post form */}
          <form onSubmit={handlePost} className="flex gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              maxLength={50}
              className="w-28 bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm text-white placeholder-[#666] focus:border-[#FF3B30] outline-none"
              data-testid={`comment-name-${itemId}`}
            />
            <input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Leave a comment..."
              maxLength={1000}
              className="flex-1 bg-[#0A0A0A] border border-[#262626] px-3 py-2 text-sm text-white placeholder-[#666] focus:border-[#FF3B30] outline-none"
              data-testid={`comment-text-${itemId}`}
            />
            <button
              type="submit"
              disabled={posting || !name.trim() || !text.trim()}
              className="px-3 py-2 bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              data-testid={`comment-submit-${itemId}`}
            >
              <Send className="w-4 h-4" />
            </button>
          </form>

          {/* Comments list */}
          {comments.length === 0 ? (
            <p className="text-xs text-[#666]">No comments yet. Be the first!</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {comments.map(c => (
                <div key={c.id} className="flex items-start gap-3 bg-[#0A0A0A] border border-[#1a1a1a] px-3 py-2 group">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-[#FF3B30]">{c.name}</span>
                      <span className="text-xs text-[#666]">{timeAgo(c.created_at)}</span>
                    </div>
                    <p className="text-sm text-[#ccc] mt-0.5">{c.text}</p>
                  </div>
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="opacity-0 group-hover:opacity-100 text-[#666] hover:text-[#FF3B30] transition-all flex-shrink-0"
                      data-testid={`delete-comment-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
